/* ==========================================================================
   Composer theme registry.
   --------------------------------------------------------------------------
   Mirrors the registry in `theme-switcher.js` at the repo root. Both files
   point at the same `themes/*.css` assets that `scripts/sync-assets.js`
   copies into `public/themes/` at dev / build time, so the static gallery
   and the composer surface the exact same set of themes.
   --------------------------------------------------------------------------
   To add a theme: drop a new file under repo-root `themes/`, add a row
   here AND to `theme-switcher.js`. The composer's UI groups by `family`
   automatically.
   ========================================================================== */

export interface ThemeEntry {
  /** Path used as the stylesheet href -- '' = no override (cream default). */
  id: string;
  /** Short human label shown in the picker. */
  label: string;
  /** Aesthetic family used for grouping in the inspector. */
  family: string;
  /** Optional sub-variant label rendered next to the name. */
  variant?: string;
}

export const THEME_FAMILY_ORDER: string[] = [
  'Foundation',
  'Slate', 'Playful', 'Feminine', 'Mono', 'Plum', 'Gamer', 'Manuscript',
  'Blueprint', 'Botanical', 'Marine',
  'Mini',
  'Fresh',
];

export const THEMES: ThemeEntry[] = [
  /* Foundation -- the original cream paper */
  { id: '',                              label: 'Cream (Default)',  family: 'Foundation' },

  /* Core families -- main branch baseline */
  { id: 'themes/slate.css',              label: 'Slate',            family: 'Slate' },
  { id: 'themes/playful.css',            label: 'Playful',          family: 'Playful' },
  { id: 'themes/feminine.css',           label: 'Feminine',         family: 'Feminine' },
  { id: 'themes/mono.css',               label: 'Mono',             family: 'Mono' },
  { id: 'themes/plum.css',               label: 'Plum',             family: 'Plum' },
  { id: 'themes/gamer.css',              label: 'Gamer',            family: 'Gamer' },
  { id: 'themes/manuscript.css',         label: 'Manuscript',       family: 'Manuscript' },

  /* Canvas variants (slim canvas/paper-inset system) */
  { id: 'themes/blueprint.css',          label: 'Blueprint',        family: 'Blueprint' },
  { id: 'themes/botanical.css',          label: 'Botanical',        family: 'Botanical' },
  { id: 'themes/marine.css',             label: 'Marine',           family: 'Marine' },
  { id: 'themes/blueprint-canvas.css',   label: 'Blueprint Canvas', family: 'Blueprint',  variant: 'Canvas' },
  { id: 'themes/botanical-canvas.css',   label: 'Botanical Canvas', family: 'Botanical',  variant: 'Canvas' },
  { id: 'themes/marine-canvas.css',      label: 'Marine Canvas',    family: 'Marine',     variant: 'Canvas' },
  { id: 'themes/manuscript-canvas.css',  label: 'Manuscript Canvas',family: 'Manuscript', variant: 'Canvas' },
  { id: 'themes/mono-canvas.css',        label: 'Mono Canvas',      family: 'Mono',       variant: 'Canvas' },
  { id: 'themes/playful-canvas.css',     label: 'Playful Canvas',   family: 'Playful',    variant: 'Canvas' },
  { id: 'themes/plum-canvas.css',        label: 'Plum Canvas',      family: 'Plum',       variant: 'Canvas' },
  { id: 'themes/slate-canvas.css',       label: 'Slate Canvas',     family: 'Slate',      variant: 'Canvas' },

  /* Bold variants -- full-bleed paper colors */
  { id: 'themes/blueprint-bold.css',     label: 'Blueprint Bold',   family: 'Blueprint',  variant: 'Bold' },
  { id: 'themes/botanical-bold.css',     label: 'Botanical Bold',   family: 'Botanical',  variant: 'Bold' },
  { id: 'themes/manuscript-bold.css',    label: 'Manuscript Bold',  family: 'Manuscript', variant: 'Bold' },
  { id: 'themes/marine-bold.css',        label: 'Marine Bold',      family: 'Marine',     variant: 'Bold' },
  { id: 'themes/mono-bold.css',          label: 'Mono Bold',        family: 'Mono',       variant: 'Bold' },
  { id: 'themes/playful-bold.css',       label: 'Playful Bold',     family: 'Playful',    variant: 'Bold' },
  { id: 'themes/plum-bold.css',          label: 'Plum Bold',        family: 'Plum',       variant: 'Bold' },
  { id: 'themes/slate-bold.css',         label: 'Slate Bold',       family: 'Slate',      variant: 'Bold' },

  /* Studio variants -- drafting-room / book composition */
  { id: 'themes/manuscript-studio.css',  label: 'Manuscript Studio',family: 'Manuscript', variant: 'Studio' },
  { id: 'themes/mono-studio.css',        label: 'Mono Studio',      family: 'Mono',       variant: 'Studio' },
  { id: 'themes/playful-studio.css',     label: 'Playful Studio',   family: 'Playful',    variant: 'Studio' },
  { id: 'themes/plum-studio.css',        label: 'Plum Studio',      family: 'Plum',       variant: 'Studio' },
  { id: 'themes/slate-studio.css',       label: 'Slate Studio',     family: 'Slate',      variant: 'Studio' },

  /* Editorial variants -- cyberpunk gamer, illuminated codex... */
  { id: 'themes/feminine-editorial.css', label: 'Feminine Editorial',  family: 'Feminine',   variant: 'Editorial' },
  { id: 'themes/gamer-editorial.css',    label: 'Gamer Editorial',     family: 'Gamer',      variant: 'Editorial' },
  { id: 'themes/manuscript-editorial.css',label:'Manuscript Editorial', family:'Manuscript', variant: 'Editorial' },
  { id: 'themes/mono-editorial.css',     label: 'Mono Editorial',      family: 'Mono',       variant: 'Editorial' },
  { id: 'themes/playful-editorial.css',  label: 'Playful Editorial',   family: 'Playful',    variant: 'Editorial' },
  { id: 'themes/plum-editorial.css',     label: 'Plum Editorial',      family: 'Plum',       variant: 'Editorial' },
  { id: 'themes/slate-editorial.css',    label: 'Slate Editorial',     family: 'Slate',      variant: 'Editorial' },

  /* Boutique variants -- high-end stationery boutique */
  { id: 'themes/feminine-boutique.css',  label: 'Feminine Boutique',   family: 'Feminine',   variant: 'Boutique' },
  { id: 'themes/gamer-boutique.css',     label: 'Gamer Boutique',      family: 'Gamer',      variant: 'Boutique' },
  { id: 'themes/manuscript-boutique.css',label: 'Manuscript Boutique', family: 'Manuscript', variant: 'Boutique' },
  { id: 'themes/mono-boutique.css',      label: 'Mono Boutique',       family: 'Mono',       variant: 'Boutique' },
  { id: 'themes/playful-boutique.css',   label: 'Playful Boutique',    family: 'Playful',    variant: 'Boutique' },
  { id: 'themes/plum-boutique.css',      label: 'Plum Boutique',       family: 'Plum',       variant: 'Boutique' },
  { id: 'themes/slate-boutique.css',     label: 'Slate Boutique',      family: 'Slate',      variant: 'Boutique' },

  /* Mini -- minimal accent overlays */
  { id: 'themes/teal-mini.css',          label: 'Teal Mini',        family: 'Mini' },
  { id: 'themes/iris-mini.css',          label: 'Iris Mini',        family: 'Mini' },

  /* Fresh -- new originals */
  { id: 'themes/sunset.css',             label: 'Sunset',           family: 'Fresh' },
  { id: 'themes/forest.css',             label: 'Forest',           family: 'Fresh' },
  { id: 'themes/ocean.css',              label: 'Ocean',            family: 'Fresh' },
  { id: 'themes/aurora.css',             label: 'Aurora',           family: 'Fresh' },
  { id: 'themes/charcoal.css',           label: 'Charcoal',         family: 'Fresh' },
  { id: 'themes/linen.css',              label: 'Linen',            family: 'Fresh' },
  { id: 'themes/sakura.css',             label: 'Sakura',           family: 'Fresh' },
  { id: 'themes/citrus.css',             label: 'Citrus',           family: 'Fresh' },
  { id: 'themes/midnight.css',           label: 'Midnight',         family: 'Fresh' },
  { id: 'themes/coral.css',              label: 'Coral',            family: 'Fresh' },
  { id: 'themes/mint.css',               label: 'Mint',             family: 'Fresh' },
  { id: 'themes/espresso.css',           label: 'Espresso',         family: 'Fresh' },
  { id: 'themes/blossom.css',            label: 'Blossom',          family: 'Fresh' },
  { id: 'themes/steel.css',              label: 'Steel',            family: 'Fresh' },
  { id: 'themes/lavender.css',           label: 'Lavender',         family: 'Fresh' },
  { id: 'themes/goldleaf.css',           label: 'Goldleaf',         family: 'Fresh' },
  { id: 'themes/monaco.css',             label: 'Monaco',           family: 'Fresh' },
  { id: 'themes/neon.css',               label: 'Neon',             family: 'Fresh' },
  { id: 'themes/terracotta.css',         label: 'Terracotta',       family: 'Fresh' },
];

export const THEMES_BY_ID: Map<string, ThemeEntry> = new Map(THEMES.map((t) => [t.id, t]));

export function findTheme(id: string | undefined | null): ThemeEntry {
  if (!id) return THEMES[0];
  return THEMES_BY_ID.get(id) || THEMES[0];
}

export function groupThemes(list: ThemeEntry[] = THEMES): [string, ThemeEntry[]][] {
  const map = new Map<string, ThemeEntry[]>();
  for (const t of list) {
    if (!map.has(t.family)) map.set(t.family, []);
    map.get(t.family)!.push(t);
  }
  const ordered: [string, ThemeEntry[]][] = [];
  for (const fam of THEME_FAMILY_ORDER) {
    if (map.has(fam)) {
      ordered.push([fam, map.get(fam)!]);
      map.delete(fam);
    }
  }
  for (const [fam, items] of map) ordered.push([fam, items]);
  return ordered;
}
